import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Lock, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { BrandPanel } from './Login';
import { passwordResetConfirm } from '@/services/authClient';

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({ password: '', confirmPassword: '' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (form.password.length < 8) errs.password = 'Password must be at least 8 characters';
    if (form.password !== form.confirmPassword) errs.confirmPassword = 'Passwords do not match';
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setLoading(true);
    try {
      await passwordResetConfirm(token, form.password);
      setSuccess(true);
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Invalid or expired reset link';
      setErrors({ api: msg });
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="grid min-h-[calc(100vh-4rem)] lg:grid-cols-2">
        <BrandPanel />
        <div className="flex items-center justify-center p-6 sm:p-10">
          <div className="w-full max-w-md text-center">
            <div className="mx-auto mb-6 grid h-16 w-16 place-items-center rounded-full bg-green-50">
              <CheckCircle size={32} className="text-green-600" />
            </div>
            <h1 className="font-display text-2xl font-bold text-slate-900">Password reset complete</h1>
            <p className="mt-2 text-slate-600">Your password has been updated. Redirecting to sign in…</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="grid min-h-[calc(100vh-4rem)] lg:grid-cols-2">
      <BrandPanel />
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <h1 className="font-display text-3xl font-bold text-slate-900">Reset password</h1>
          <p className="mt-1 text-slate-600">Choose a new password for your account.</p>
          {errors.api && <p className="mt-4 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-600">{errors.api}</p>}
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="password">New password</Label>
              <div className="relative mt-1.5">
                <Lock size={16} className="absolute left-3 top-3 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="Create a new password"
                  className="rounded-xl pl-9"
                />
              </div>
              {errors.password && <p className="mt-1 text-xs text-red-600">{errors.password}</p>}
            </div>
            <div>
              <Label htmlFor="confirmPassword">Confirm password</Label>
              <div className="relative mt-1.5">
                <Lock size={16} className="absolute left-3 top-3 text-slate-400" />
                <Input
                  id="confirmPassword"
                  type="password"
                  value={form.confirmPassword}
                  onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                  placeholder="Re-enter your password"
                  className="rounded-xl pl-9"
                />
              </div>
              {errors.confirmPassword && <p className="mt-1 text-xs text-red-600">{errors.confirmPassword}</p>}
            </div>
            <Button type="submit" className="w-full rounded-xl" disabled={loading}>
              {loading ? 'Resetting…' : 'Reset password'}
            </Button>
          </form>
          <p className="mt-6 text-center text-sm text-slate-600">
            <Link to="/login" className="font-medium text-blue-600 hover:underline">Back to sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
